/**
 * usePostcodeLookup Hook
 * Reusable hook for postcode-based location auto-fill functionality
 *
 * Usage:
 * 1. Pass the country field value and setValue function from react-hook-form
 * 2. Call handlePostcodeChange in the postcode input's onChange
 * 3. The hook will automatically lookup and fill state/city fields
 */

import { useCallback, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { App } from 'antd';
import { locationApi } from '@/services/api/location.api';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';

// board2-#79 (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): the single-address
// rework's MOCK parse helper lives in its own pure module (node-testable, no
// react/antd deps) and is re-exported here as part of the postcode-lookup
// surface. Mock handlers use it to SIMULATE the backend Google-Maps split;
// the real geocode is backend scope.
export {
  mockParseAddress,
  type AddressParseStatus,
  type MockParseAddressInput,
  type MockParseAddressDataset,
  type MockParseAddressState,
  type MockParseAddressCity,
  type MockParseAddressResult,
} from './mockParseAddress';

/** Location info returned from postcode lookup */
export interface PostcodeLookupLocation {
  stateId: number;
  stateName: string;
  cityId: number;
  cityName: string;
}

export interface PostcodeLookupConfig {
  /** Current selected country code (e.g., 'TW', 'AU') */
  countryCode: string | undefined;
  /** react-hook-form setValue function */
  setValue: (name: string, value: unknown, options?: { shouldTouch?: boolean; shouldDirty?: boolean; shouldValidate?: boolean }) => void;
  /** Field name for state ID (default: 'stateId') */
  stateIdField?: string;
  /** Field name for state label (default: 'state') */
  stateLabelField?: string;
  /** Field name for city ID (default: 'cityId') */
  cityIdField?: string;
  /** Field name for city label (default: 'city') */
  cityLabelField?: string;
  /** Minimum postcode length to trigger lookup (default: 3) */
  minLength?: number;
  /** Debounce wait time in ms (default: 500) */
  debounceWait?: number;
  /** Whether to show success notification (default: true) */
  showNotification?: boolean;
}

export interface PostcodeLookupResult {
  /** Handler to call on postcode input change */
  handlePostcodeChange: (postcode: string) => void;
  /** Handler to call on postcode input blur (immediate lookup) */
  handlePostcodeBlur: (postcode: string) => void;
  /** Whether a lookup is currently in progress */
  isLookingUp: boolean;
  /** Location data from the last successful lookup (use for fallback display) */
  foundLocation: PostcodeLookupLocation | null;
  /** Clear the found location data */
  clearFoundLocation: () => void;
}

/**
 * Hook for automatic postcode-based location lookup
 *
 * @example
 * ```tsx
 * const { handlePostcodeChange, handlePostcodeBlur } = usePostcodeLookup({
 *   countryCode: watch('country'),
 *   setValue,
 *   stateIdField: 'stateId',
 *   stateLabelField: 'state',
 *   cityIdField: 'cityId',
 *   cityLabelField: 'city',
 * });
 *
 * <Input
 *   onChange={(e) => {
 *     field.onChange(e);
 *     handlePostcodeChange(e.target.value);
 *   }}
 *   onBlur={() => handlePostcodeBlur(field.value)}
 * />
 * ```
 */
export function usePostcodeLookup({
  countryCode,
  setValue,
  stateIdField = 'stateId',
  stateLabelField = 'state',
  cityIdField = 'cityId',
  cityLabelField = 'city',
  minLength = 3,
  debounceWait = 500,
  showNotification = true,
}: PostcodeLookupConfig): PostcodeLookupResult {
  const { notification } = App.useApp();
  const { t } = useVendorTranslation();

  // Track the found location for fallback display
  const [foundLocation, setFoundLocation] = useState<PostcodeLookupLocation | null>(null);

  // Core lookup function
  const lookupPostcode = useCallback(
    async (postcode: string) => {
      if (!countryCode || !postcode || postcode.length < minLength) return;

      try {
        const response = await locationApi.lookupByPostcode({
          countryCode,
          postcode,
        });

        if (response.data && response.data.length > 0) {
          const location = response.data[0];

          // Store found location for fallback display in Select components
          setFoundLocation({
            stateId: location.stateId,
            stateName: location.stateName,
            cityId: location.cityId,
            cityName: location.cityName,
          });

          // Auto-fill state - mark as touched so auto-create location can trigger
          if (location.stateId) {
            setValue(stateIdField, location.stateId, { shouldTouch: true });
            if (location.stateName) {
              setValue(stateLabelField, location.stateName, { shouldTouch: true });
            }
          }

          // Auto-fill city - mark as touched so auto-create location can trigger
          if (location.cityId) {
            setValue(cityIdField, location.cityId, { shouldTouch: true });
            if (location.cityName) {
              setValue(cityLabelField, location.cityName, { shouldTouch: true });
            }
          }

          // Show success notification
          if (showNotification) {
            notification.success({
              message: t('vendor.notifications.locationFound'),
              description: t('vendor.notifications.locationAutoFilled', {
                state: location.stateName,
                city: location.cityName,
              }),
              placement: 'topRight',
              duration: 2,
            });
          }
        }
      } catch (error) {
        console.error('Postcode lookup failed:', error);
        // Silently fail - user can still select manually
      }
    },
    [
      countryCode,
      setValue,
      stateIdField,
      stateLabelField,
      cityIdField,
      cityLabelField,
      minLength,
      showNotification,
      notification,
      t,
    ]
  );

  // Clear found location (call when user manually changes state/city)
  const clearFoundLocation = useCallback(() => {
    setFoundLocation(null);
  }, []);

  // Debounced version for onChange (while typing)
  // cancel() is used to prevent duplicate API calls when blur happens
  const { run: debouncedLookup, cancel: cancelDebouncedLookup } = useDebounceFn(lookupPostcode, {
    wait: debounceWait,
  });

  // Handler for onChange - debounced
  const handlePostcodeChange = useCallback(
    (postcode: string) => {
      debouncedLookup(postcode);
    },
    [debouncedLookup]
  );

  // Handler for onBlur - immediate lookup
  // Cancels any pending debounced call to prevent duplicate API requests
  const handlePostcodeBlur = useCallback(
    (postcode: string) => {
      cancelDebouncedLookup(); // Cancel pending debounced call
      lookupPostcode(postcode);
    },
    [lookupPostcode, cancelDebouncedLookup]
  );

  return {
    handlePostcodeChange,
    handlePostcodeBlur,
    isLookingUp: false, // Could be enhanced with loading state if needed
    foundLocation,
    clearFoundLocation,
  };
}

export default usePostcodeLookup;
